import { createPortal } from "react-dom";
import { useEffect, useRef, useState, useCallback } from "react";
import { X, UserPlus, UserCheck, MessageCircle, ExternalLink, BadgeCheck, Sparkles } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import VipBadge from "@/components/VipBadge";
import LifetimeBossBadge from "@/components/LifetimeBossBadge";
import TopBadge from "@/components/TopBadge";

/**
 * UserPreviewSheet — the shared in-place profile preview.
 *
 * Rendered via React Portal directly on `document.body` so it sits ABOVE
 * every other in-Live layer (LiveRoomStage z-80, BattleSplitLayout, etc.)
 * without unmounting them. That's what lets the Live keep playing behind
 * the sheet: LiveKitRoom stays mounted, audio-bridge subscribers stay
 * mounted, WebSocket stays connected. This component only paints; it
 * doesn't touch any Live infrastructure.
 *
 * Design directive: "clean by default, powerful on demand" — avatar,
 * name/@handle, badges, short bio, follower/following counts, then three
 * clear actions (Follow, Message, View Full Profile). No chrome, no
 * secondary panels, no extra CTAs. If a viewer wants more, that's what
 * View Full Profile is for.
 */
export default function UserPreviewSheet({ userRef, open, onClose }) {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const sheetRef = useRef(null);
  const previouslyFocused = useRef(null);

  // Load preview data when we open. Reset state when we close so a stale
  // record from the previous open doesn't flash on the next one.
  useEffect(() => {
    if (!open || !userRef) {
      setData(null); setErr(""); setLoading(false); setBusy(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const r = await api.get(`/users/${encodeURIComponent(userRef)}/preview`);
        if (!cancelled) setData(r.data);
      } catch (e) {
        if (!cancelled) setErr(e?.response?.data?.detail || "Couldn't load profile.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open, userRef]);

  // Focus management + Esc-to-close. We do NOT trap focus aggressively
  // because the Live underneath may still receive taps; but we do restore
  // focus to whatever opened the sheet on close, so keyboard users don't
  // get lost.
  useEffect(() => {
    if (!open) return;
    previouslyFocused.current = document.activeElement;
    const onKey = (e) => { if (e.key === "Escape") onClose?.(); };
    document.addEventListener("keydown", onKey);
    // Give the sheet's close button initial focus so keyboard users can
    // dismiss immediately without having to Tab into it.
    setTimeout(() => sheetRef.current?.querySelector("[data-testid='user-preview-close']")?.focus(), 60);
    return () => {
      document.removeEventListener("keydown", onKey);
      try { previouslyFocused.current?.focus?.(); } catch { /* stale ref is fine */ }
    };
  }, [open, onClose]);

  const handleFollow = useCallback(async () => {
    if (!data || busy || data.is_self) return;
    setBusy(true);
    try {
      if (data.is_followed_by_me) {
        await api.delete(`/users/${data.id}/follow`);
        setData((d) => d && { ...d, is_followed_by_me: false, followers_count: Math.max(0, (d.followers_count || 0) - 1) });
      } else {
        await api.post(`/users/${data.id}/follow`);
        setData((d) => d && { ...d, is_followed_by_me: true, followers_count: (d.followers_count || 0) + 1 });
      }
    } catch (e) {
      setErr(e?.response?.data?.detail || "Couldn't update follow.");
    } finally {
      setBusy(false);
    }
  }, [data, busy]);

  const handleMessage = useCallback(() => {
    if (!data) return;
    // Close FIRST so React doesn't unmount our navigate() below with
    // an inconsistent state. `/inbox?to=handle` is the existing DM entry
    // point in this codebase — we're not creating a new UX pattern.
    onClose?.();
    navigate(`/inbox?to=${data.id}`);
  }, [data, navigate, onClose]);

  const handleFullProfile = useCallback(() => {
    if (!data) return;
    onClose?.();
    navigate(`/u/${data.id}`);
  }, [data, navigate, onClose]);

  if (!open) return null;

  return createPortal(
    <div
      className="fixed inset-0 z-[90] flex items-end sm:items-center justify-center"
      data-testid="user-preview-sheet-root"
    >
      {/* Semi-transparent backdrop — Live behind stays partially visible so
          the viewer feels like they're still inside the room. Tap-to-close. */}
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={onClose}
        data-testid="user-preview-backdrop"
      />
      <div
        ref={sheetRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="user-preview-title"
        className="relative w-full sm:w-[380px] max-h-[80vh] sm:max-h-[520px] bg-zinc-950 border border-white/10 shadow-2xl shadow-black/60 rounded-t-2xl sm:rounded-2xl overflow-hidden animate-in slide-in-from-bottom-4 fade-in duration-200"
        style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
        data-testid="user-preview-sheet"
      >
        {/* Close */}
        <button
          type="button"
          onClick={onClose}
          data-testid="user-preview-close"
          aria-label="Close profile preview"
          className="absolute top-2 right-2 z-10 w-8 h-8 flex items-center justify-center rounded-full bg-black/40 text-white/80 hover:text-white hover:bg-black/60 transition-colors"
        >
          <X size={16} />
        </button>

        {loading && (
          <div className="p-8 text-center text-sm text-zinc-400" data-testid="user-preview-loading">
            Loading…
          </div>
        )}
        {err && !loading && (
          <div className="p-8 text-center text-sm text-rose-300" data-testid="user-preview-error">
            {err}
          </div>
        )}
        {data && !loading && !err && (
          <SheetContent
            data={data}
            busy={busy}
            onFollow={handleFollow}
            onMessage={handleMessage}
            onFullProfile={handleFullProfile}
          />
        )}
      </div>
    </div>,
    document.body,
  );
}

function SheetContent({ data, busy, onFollow, onMessage, onFullProfile }) {
  const followBtnLabel = data.is_followed_by_me ? "Following" : "Follow";
  const FollowIcon = data.is_followed_by_me ? UserCheck : UserPlus;
  return (
    <div className="p-5">
      {/* Avatar + name row */}
      <div className="flex items-start gap-4">
        <img
          src={data.avatar_url}
          alt={data.display_name}
          className="w-16 h-16 rounded-full object-cover border-2 border-white/10 bg-zinc-900"
          data-testid="user-preview-avatar"
          loading="lazy"
        />
        <div className="flex-1 min-w-0 pt-1">
          <div className="flex items-center gap-1.5 flex-wrap">
            <h2
              id="user-preview-title"
              className="text-white text-lg font-semibold truncate"
              data-testid="user-preview-display-name"
            >
              {data.display_name}
            </h2>
            {data.is_ambassador && <BadgeCheck size={16} className="text-rose-400" data-testid="user-preview-badge-ambassador" />}
            {data.is_premium && <Sparkles size={14} className="text-amber-400" data-testid="user-preview-badge-premium" />}
            <LifetimeBossBadge show={data.lifetime_boss} size="xs" />
            <VipBadge tier={data.vip_tier} size="xs" />
            <TopBadge show={data.top_badge} size="xs" />
          </div>
          <div className="text-zinc-400 text-sm mt-0.5 truncate" data-testid="user-preview-handle">
            {data.display_name}
          </div>
          {data.is_live && (
            <div
              className="inline-flex items-center gap-1 mt-1.5 px-1.5 py-0.5 text-[10px] uppercase tracking-widest text-rose-200 bg-rose-500/20 border border-rose-500/40 rounded"
              data-testid="user-preview-live-badge"
            >
              <span className="w-1.5 h-1.5 bg-rose-400 rounded-full animate-pulse" />
              Live now
            </div>
          )}
        </div>
      </div>

      {/* Bio — 2-line clamp keeps the sheet compact */}
      {data.bio && (
        <p
          className="text-zinc-300 text-sm mt-4 leading-snug line-clamp-2"
          data-testid="user-preview-bio"
        >
          {data.bio}
        </p>
      )}

      {/* Follower / Following counts */}
      <div className="flex items-center gap-6 mt-4 text-xs">
        <div data-testid="user-preview-followers">
          <div className="text-white font-semibold text-sm tabular-nums">{Number(data.followers_count || 0).toLocaleString()}</div>
          <div className="text-zinc-500 uppercase tracking-widest text-[10px]">Followers</div>
        </div>
        <div data-testid="user-preview-following">
          <div className="text-white font-semibold text-sm tabular-nums">{Number(data.following_count || 0).toLocaleString()}</div>
          <div className="text-zinc-500 uppercase tracking-widest text-[10px]">Following</div>
        </div>
        {data.bamboo_lifetime_earned != null && (data.is_creator || data.bamboo_lifetime_earned > 0) && (
          <div data-testid="user-preview-bamboo-earned" title="Lifetime Bamboo earned from gifts">
            <div className="text-white font-semibold text-sm tabular-nums">{Number(data.bamboo_lifetime_earned).toLocaleString()} 🎋</div>
            <div className="text-zinc-500 uppercase tracking-widest text-[10px]">Earned</div>
          </div>
        )}
      </div>

      {/* Actions — three, no more. */}
      <div className="grid grid-cols-3 gap-2 mt-5">
        {!data.is_self && (
          <button
            type="button"
            onClick={onFollow}
            disabled={busy}
            data-testid="user-preview-follow-btn"
            className={`h-10 flex items-center justify-center gap-1.5 text-xs font-medium rounded-lg transition-colors ${
              data.is_followed_by_me
                ? "bg-white/5 text-zinc-200 border border-white/10 hover:bg-white/10"
                : "bg-rose-600 text-white hover:bg-rose-500"
            } disabled:opacity-60`}
          >
            <FollowIcon size={14} /> {followBtnLabel}
          </button>
        )}
        {!data.is_self && (
          <button
            type="button"
            onClick={onMessage}
            data-testid="user-preview-message-btn"
            className="h-10 flex items-center justify-center gap-1.5 text-xs font-medium rounded-lg bg-white/5 text-zinc-200 border border-white/10 hover:bg-white/10 transition-colors"
          >
            <MessageCircle size={14} /> Message
          </button>
        )}
        <button
          type="button"
          onClick={onFullProfile}
          data-testid="user-preview-full-profile-btn"
          className={`h-10 flex items-center justify-center gap-1.5 text-xs font-medium rounded-lg bg-white/5 text-zinc-200 border border-white/10 hover:bg-white/10 transition-colors ${data.is_self ? "col-span-3" : ""}`}
        >
          <ExternalLink size={14} /> View Full Profile
        </button>
      </div>
    </div>
  );
}
